import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, apiRaw } from '../services/api';
import { useApi } from '../hooks/useApi';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import type { Project, TimeEntry } from '../types';
import { PieChart, FileSpreadsheet, FileText, BarChart3 } from 'lucide-react';

export default function ReportsPage() {
  const { t } = useTranslation();
  const { data: projects } = useApi<Project[]>(() => api('/projects'), []);
  const [projectId, setProjectId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [reportData, setReportData] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const fetchReport = async () => {
    setError(''); setBusy(true);
    const params = new URLSearchParams();
    if (projectId) params.set('projectId', projectId);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (groupBy) params.set('groupBy', groupBy);
    try { const data = await api<any[]>(`/reports?${params}`); setReportData(data || []); }
    catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  };

  const exportExcel = async () => {
    if (!projectId) return setError('Select a project for export');
    const yearMonth = startDate ? startDate.slice(0, 7) : new Date().toISOString().slice(0, 7);
    try {
      const res = await apiRaw(`/exports/excel?projectId=${projectId}&yearMonth=${yearMonth}`);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `report-${yearMonth}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (err: any) { setError(err.message); }
  };

  const exportCsv = async () => {
    if (!projectId) return setError('Select a project for export');
    const yearMonth = startDate ? startDate.slice(0, 7) : new Date().toISOString().slice(0, 7);
    try {
      const res = await apiRaw(`/exports/csv?projectId=${projectId}&yearMonth=${yearMonth}`);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `report-${yearMonth}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (err: any) { setError(err.message); }
  };

  return (
    <div className="animate-in space-y-6">
      <PageHeader title={t('reports') || 'Reports'} description="Generate timesheet reports and export data">
        <div className="flex items-center gap-2">
          <Button data-testid="export-excel-button" variant="outline" size="sm" className="gap-2" onClick={exportExcel}><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
          <Button data-testid="export-csv-button" variant="outline" size="sm" className="gap-2" onClick={exportCsv}><FileText className="h-4 w-4" /> CSV</Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><PieChart className="h-4 w-4 text-[var(--color-primary)]" /> Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px] space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">{t('project')}</label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger><SelectValue placeholder="All projects" /></SelectTrigger>
                <SelectContent>{(projects || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">From</label>
              <Input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">To</label>
              <Input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">{t('groupBy') || 'Group by'}</label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-40"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="person">Person</SelectItem><SelectItem value="task">Task</SelectItem><SelectItem value="date">Date</SelectItem></SelectContent>
              </Select>
            </div>
            <Button data-testid="generate-report-button" onClick={fetchReport} loading={busy} disabled={busy} className="gap-2"><BarChart3 className="h-4 w-4" /> {t('generate') || 'Generate'}</Button>
          </div>
          {error && <Badge variant="destructive" className="mt-3">{error}</Badge>}
        </CardContent>
      </Card>

      {busy && <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div>}

      {reportData.length > 0 && (
        <Badge className="mb-2">{reportData.length} rows</Badge>
      )}

      <Card className="overflow-hidden rounded-lg border shadow-sm">
        {reportData.length === 0 ? (
          <EmptyState icon={PieChart} title={t('noData') || 'No report data'} description="Select filters and generate a report to see results." />
        ) : (
          <>
            <div className="hidden lg:block">
              <Table>
                <TableHeader>
                  {groupBy === 'person' ? (
                    <TableRow>
                      <TableHead>Person</TableHead>
                      <TableHead>Total Hours</TableHead>
                      <TableHead>Entries</TableHead>
                    </TableRow>
                  ) : groupBy === 'task' ? (
                    <TableRow>
                      <TableHead>Task</TableHead>
                      <TableHead>Total Hours</TableHead>
                      <TableHead>Entries</TableHead>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Task</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  )}
                </TableHeader>
                <TableBody>
                  {reportData.map((row: any, i: number) => {
                    if (groupBy === 'person') {
                      return (
                        <TableRow key={i}>
                          <TableCell>{row.user?.name}</TableCell>
                          <TableCell className="font-mono">{(row.totalMinutes / 60).toFixed(2)}h</TableCell>
                          <TableCell>{row.entries?.length ?? 0}</TableCell>
                        </TableRow>
                      );
                    }
                    if (groupBy === 'task') {
                      return (
                        <TableRow key={i}>
                          <TableCell>{row.task?.name}</TableCell>
                          <TableCell className="font-mono">{(row.totalMinutes / 60).toFixed(2)}h</TableCell>
                          <TableCell>{row.entries?.length ?? 0}</TableCell>
                        </TableRow>
                      );
                    }
                    const entry = row as TimeEntry;
                    return (
                      <TableRow key={i}>
                        <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                        <TableCell>{entry.user?.name}</TableCell>
                        <TableCell>{entry.project?.name}</TableCell>
                        <TableCell>{entry.task?.name}</TableCell>
                        <TableCell className="font-mono text-[var(--color-primary)] font-medium">{(entry.minutes / 60).toFixed(2)}h</TableCell>
                        <TableCell className="text-[var(--color-muted-foreground)] text-xs">{entry.note}</TableCell>
                        <TableCell><Badge variant={entry.status === 'APPROVED' ? 'success' : entry.status === 'DRAFT' ? 'default' : 'outline'}>{entry.status}</Badge></TableCell>
                        <TableCell className="text-right font-mono text-xs">{entry.amountChf != null ? `${entry.amountChf.toFixed(2)} CHF` : '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 lg:hidden p-4">
              {reportData.map((row: any, i: number) => {
                if (groupBy === 'person') {
                  return (
                    <div key={i} className="rounded-lg border bg-white p-3 space-y-1">
                      <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Person</span><span className="font-medium">{row.user?.name}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Total Hours</span><span className="font-mono">{(row.totalMinutes / 60).toFixed(2)}h</span></div>
                      <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Entries</span><span>{row.entries?.length ?? 0}</span></div>
                    </div>
                  );
                }
                if (groupBy === 'task') {
                  return (
                    <div key={i} className="rounded-lg border bg-white p-3 space-y-1">
                      <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Task</span><span className="font-medium">{row.task?.name}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Total Hours</span><span className="font-mono">{(row.totalMinutes / 60).toFixed(2)}h</span></div>
                      <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Entries</span><span>{row.entries?.length ?? 0}</span></div>
                    </div>
                  );
                }
                const entry = row as TimeEntry;
                return (
                  <div key={i} className="rounded-lg border bg-white p-3 space-y-1">
                    <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Date</span><span>{new Date(entry.date).toLocaleDateString()}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">User</span><span>{entry.user?.name}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Project</span><span>{entry.project?.name}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Task</span><span>{entry.task?.name}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Duration</span><span className="font-mono font-medium">{(entry.minutes / 60).toFixed(2)}h</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-foreground)]">Note</span><span className="text-xs">{entry.note}</span></div>
                    <div className="flex justify-between text-sm items-center"><span className="text-[var(--color-muted-foreground)]">Status</span><Badge variant={entry.status === 'APPROVED' ? 'success' : entry.status === 'DRAFT' ? 'default' : 'outline'}>{entry.status}</Badge></div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
